import { ResponseWithErrorDto } from '@models/exceptions/response-dto';
import {
  ConfirmationModalService,
  ConfirmationModalType,
} from '@shared/services/confirmation-modal.service';
import { SnackbarService } from '@shared/services/snackbar.service';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { ClashListDto } from '@models/clash/clashes/clash-list-dto';
import { SimpleCLashDto } from '@models/embate/clash-dto';
import { ClashByHourDto, GamesTableDto } from '@models/embate/games-table-dto';
import { ClashRegistrationService } from '@shared/services/competition/clash-registration/clash-registration.service';
import { StepParameterizationService } from '@shared/services/competition/step-parametrization.service';
import { MessageErrorResolver } from 'src/app/core/utilities/message-error-resolver';

@Component({
  selector: 'sigecom-competition-local-games-table',
  templateUrl: './competition-local-games-table.component.html',
  styleUrls: ['./competition-local-games-table.component.scss'],
})
export class CompetitionLocalGamesTableComponent implements OnInit, OnChanges {
  @Input() gameTable?: GamesTableDto;
  @Output() editParticipantsEmit = new EventEmitter<ClashByHourDto>();
  @Output() gameTableEdit = new EventEmitter<GamesTableDto>();
  @Output() refresh = new EventEmitter<void>();

  // Dados necessários para o modal de scoreboard
  teams?: ClashListDto;
  cheNrId!: number;
  petNrId?: number;
  modalPlacar: boolean = false;

  private readonly router = inject(Router);
  private readonly parametrizationService = inject(StepParameterizationService);
  private readonly clashService = inject(ClashRegistrationService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly confirmationModalService = inject(ConfirmationModalService);
  private readonly messageErrorResolver = inject(MessageErrorResolver);

  ngOnInit(): void {}
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['gameTable']) {
      this.gameTable = changes['gameTable'].currentValue;
    }
  }

  goToClassificationTable(i: number) {
    const clash: SimpleCLashDto =
      this.gameTable!.embListEmbates[i].chaveEmbateSimplesDto;
    this.parametrizationService
      .getStepParametrizationById$(clash.petNrId)
      .subscribe((pet) => {
        this.router
          .navigate([
            'parametrizacao-etapa/editar',
            clash.petNrId,
            'chave-grupo',
            clash.cheNrId,
            pet.petBlEdicaoBloqueada,
            'classificacao',
          ])
          .then();
      });
  }

  closeModalScoreboard() {
    this.modalPlacar = false;
    // Limpa os dados quando fecha o modal
    this.teams = undefined;
    this.refresh.emit();
  }

  deleteLine(i: number) {
    const embNrId = this.gameTable!.embListEmbates[i].embNrId;
    this.confirmationModalService.openModal({
      modalType: ConfirmationModalType.danger,
      cancelText: 'Cancelar',
      confirmText: 'Excluir',
      message: 'Deseja realmente remover',
      resourceName: 'o embate',
      callbackExecuteAction: () => {
        this.clashService.deleteClash(embNrId).subscribe({
          next: () => {
            this.snackbarService.showSuccess('Embate removido com sucesso');
            this.refresh.emit();
          },
          error: (error: ResponseWithErrorDto) => {
            this.messageErrorResolver.resolveMessage(error);
          },
        });
      },
    });
  }

  OpenModalScoreboard(info: ClashByHourDto) {
    // Converte os dados do ClashByHourDto para o formato esperado pelo modal
    this.teams = this.mapClashByHourToClashListDto(info);
    this.cheNrId = info.chaveEmbateSimplesDto?.cheNrId || 0;
    this.petNrId = info.chaveEmbateSimplesDto.petNrId;

    // Só abre o modal se conseguiu mapear os dados corretamente
    if (this.teams && this.cheNrId) {
      this.modalPlacar = true;
    }
  }

  editParticipants(i: number) {
    const valueFilter: GamesTableDto = this.gameTable!;
    const info: ClashByHourDto = valueFilter.embListEmbates[i];
    this.editParticipantsEmit.emit(info);
    this.gameTableEdit.emit(valueFilter);
  }

  goToConfrontDetails(info: ClashByHourDto) {
    if (!info.embNrId) return;

    this.router.navigate(['tabela-jogos', 'detalhes-confronto', info.embNrId]);
  }

  private mapClashByHourToClashListDto(info: ClashByHourDto): ClashListDto {
    return {
      embNrId: info.embNrId,
      embBlResultadoDefinido: info.embBlResultadoDefinido,
      embListInscricaoDto: info.embListInscricoes.map((inscricao) => ({
        iemNrId: inscricao.iemNrId,
        incTxResumo:
          inscricao.embateDto?.inscricaoEtapaDto.ineObjInscricao.incTxResumo,
        fedTxNome:
          inscricao.embateDto?.inscricaoEtapaDto.ineObjInscricao.fedTxSigla,
        embNrIdOrigem: inscricao.embNrIdOrigem,
        plaNrTotalPontos: inscricao.plaNrTotalPontos,
        embateDto: inscricao.embateDto,
      })),
    };
  }

  getAbreviacao(catTxNome: string, catTxSexo: string): string {
    if (!catTxNome) return '';
    const partes = catTxNome.split(' ');
    const primeira = partes[0].slice(0, 3);
    const segunda = catTxSexo.slice(0, 1);
    return primeira.concat(segunda).toLocaleUpperCase();
  }

  getModalidadeSigla(info: ClashByHourDto): string {
    const chave = info.chaveEmbateSimplesDto;
    const modalidade = chave?.comTxNome?.trim()?.charAt(0) ?? '';
    const naipe = chave?.catTxSexo?.trim()?.charAt(0) ?? '';
    return `${modalidade}${naipe}`.toUpperCase();
  }

  getPontos(info: ClashByHourDto, index: number): number {
    return info.embListInscricoes?.at(index)?.plaNrTotalPontos ?? 0;
  }
}
